/**
 * Evaluation Criteria and Transcript Helper Functions
 * 
 * This module provides conversation-focused evaluation criteria and
 * transcript consolidation utilities for Anam simulations.
 */

export interface EvaluationCriterion {
  criterion: string;
  weight: number;
  description: string;
}

/**
 * Get conversation-focused evaluation criteria based on agent type
 * These are the criteria used to evaluate the CONVERSATION quality,
 * separate from visual performance criteria.
 */
export function getConversationCriteria(agentType: string): EvaluationCriterion[] {
  switch (agentType) {
    case 'patient':
      return [
        {
          criterion: "History Taking",
          weight: 25,
          description: "Systematic and thorough collection of patient history including presenting complaint, HPI, past medical/surgical history, medications, allergies, social history, and family history"
        },
        {
          criterion: "Communication Skills",
          weight: 20,
          description: "Clear, empathetic communication using appropriate language, active listening, and checking patient understanding"
        },
        {
          criterion: "Clinical Reasoning",
          weight: 25,
          description: "Logical approach to diagnosis with appropriate differential considerations and clinical decision-making"
        },
        {
          criterion: "Patient Rapport",
          weight: 15,
          description: "Building trust and empathy, addressing patient concerns, and maintaining a patient-centered approach"
        },
        {
          criterion: "Professionalism",
          weight: 15,
          description: "Professional conduct, appropriate boundaries, confidentiality, and ethical practice"
        }
      ];

    case 'customer':
      return [
        {
          criterion: "Discovery & Needs Assessment",
          weight: 25,
          description: "Ability to uncover prospect's pain points, challenges, and goals through effective questioning and active listening"
        },
        {
          criterion: "Communication Skills",
          weight: 20,
          description: "Clear, professional communication, active listening, and ability to articulate value propositions effectively"
        },
        {
          criterion: "Product Positioning",
          weight: 20,
          description: "Connecting solution features and benefits to prospect's specific needs and demonstrating clear ROI"
        },
        {
          criterion: "Objection Handling",
          weight: 20,
          description: "Effectively addressing concerns and objections with empathy, evidence, and strategic responses"
        },
        {
          criterion: "Closing & Next Steps",
          weight: 15,
          description: "Advancing the conversation toward clear next steps, timeline, and mutual commitment"
        }
      ];

    case 'examiner':
      return [
        {
          criterion: "Clinical Knowledge",
          weight: 25,
          description: "Comprehensive medical knowledge, evidence-based practice, and current guideline awareness"
        },
        {
          criterion: "Clinical Reasoning",
          weight: 25,
          description: "Logical thought process, differential diagnosis approach, and clinical decision-making"
        },
        {
          criterion: "Communication",
          weight: 20,
          description: "Clear, structured responses with appropriate medical terminology and systematic presentation"
        },
        {
          criterion: "Management Planning",
          weight: 20,
          description: "Appropriate investigation and management strategies with clear rationale"
        },
        {
          criterion: "Professionalism",
          weight: 10,
          description: "Professional conduct, ethical awareness, and ability to acknowledge limitations"
        }
      ];

    default:
      // Generic fallback
      return [
        {
          criterion: "Communication Skills",
          weight: 30,
          description: "Clear and effective communication"
        },
        {
          criterion: "Knowledge & Understanding",
          weight: 30,
          description: "Demonstration of relevant knowledge"
        },
        {
          criterion: "Problem Solving",
          weight: 25,
          description: "Logical approach to challenges"
        },
        {
          criterion: "Professionalism",
          weight: 15,
          description: "Professional conduct and demeanor"
        }
      ];
  }
}

/**
 * Consolidate fragmented transcript messages into readable conversation format
 * Groups consecutive messages by role and formats for AI evaluation
 */
export function consolidateTranscript(
  messages: Array<{ role: 'user' | 'assistant'; content: string; timestamp: number }>,
  agentName: string = 'Coach'
): string {
  if (!messages || messages.length === 0) {
    return '';
  }

  let consolidated = '';
  let currentRole: 'user' | 'assistant' | '' = '';
  let currentContent = '';

  for (const msg of messages) {
    if (msg.role !== currentRole) {
      // New speaker - save previous speaker's content
      if (currentContent.trim()) {
        // Use clear labels: USER vs agent name in uppercase
        const speaker = currentRole === 'user' ? 'USER' : agentName.toUpperCase();
        consolidated += `${speaker}: ${currentContent.trim()}\n\n`;
      }
      
      // Start new speaker
      currentRole = msg.role;
      currentContent = msg.content;
    } else {
      // Same speaker continues - append content with space
      currentContent += (currentContent && !currentContent.endsWith(' ') ? ' ' : '') + msg.content;
    }
  }

  // Add final message
  if (currentContent.trim()) {
    const speaker = currentRole === 'user' ? 'USER' : agentName.toUpperCase();
    consolidated += `${speaker}: ${currentContent.trim()}`;
  }

  return consolidated;
}
